import type { FC, RefObject } from "react"
import { useCallback, useState } from "react"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconPhotoOutline18 as ImagePlay,
	IconVideoOutline18 as YoutubeIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { LoomIcon, Tooltip } from "@productlane/ui"
import { DropdownMenu } from "@productlane/ui/src/components/internal/dropdown"

import { EditorMenuButton } from "../editor-menu-button"

interface MediaDropdownProps {
	containerRef?: RefObject<HTMLDivElement | null>
	toggleAddingState: (type: "media" | "youtube" | "loom" | "link") => void
	activeState: {
		isAddingMedia: boolean
		isAddingYoutube: boolean
		isAddingLoom: boolean
	}
	onTriggerMediaUpload: () => void
}

export const EditorMenuEmbeededMediaDropdown: FC<MediaDropdownProps> = ({
	containerRef,
	toggleAddingState,
	activeState,
	onTriggerMediaUpload,
}) => {
	const [isOpen, setIsOpen] = useState(false)

	const handleMediaClick = useCallback(() => {
		setIsOpen(false)
		onTriggerMediaUpload()
	}, [onTriggerMediaUpload])

	return (
		<DropdownMenu
			trigger={
				<div>
					<EditorMenuButton isActive={false} className="flex items-center pr-1">
						<ImagePlay className="h-4 w-4" />
						<ChevronDown className="ml-auto h-3.5 w-3.5" />
					</EditorMenuButton>
				</div>
			}
			containerRef={containerRef}
			className="w-36"
			open={isOpen}
			onOpenChange={(open) => {
				setIsOpen(open)
			}}
		>
			<Tooltip shouldWrapChildren label="Media">
				<EditorMenuButton
					onClick={handleMediaClick}
					isActive={false}
					className="w-full"
				>
					<div className="flex w-full items-center gap-2">
						<ImagePlay className="h-4 w-4" />
						<div className="truncate text-xs text-primary">Media</div>
					</div>
				</EditorMenuButton>
			</Tooltip>

			<Tooltip shouldWrapChildren label="Loom">
				<EditorMenuButton
					onClick={(event) => {
						event.preventDefault()
						toggleAddingState("loom")
						setIsOpen(false)
					}}
					isActive={activeState.isAddingLoom}
					className={cn(
						"w-full",
						activeState.isAddingLoom
							? `fill-black text-xl text-black dark:fill-gray-100 dark:text-gray-100`
							: `fill-gray-500 text-xl text-gray-500 hover:fill-gray-800 hover:text-gray-800 dark:fill-gray-400 dark:text-gray-400 dark:hover:fill-gray-100 dark:hover:text-gray-100`,
					)}
				>
					<div className="flex w-full items-center gap-2">
						<LoomIcon className={cn("h-4 w-4")} />
						<div className="truncate text-xs text-primary">Loom video</div>
					</div>
				</EditorMenuButton>
			</Tooltip>

			<Tooltip shouldWrapChildren label="Youtube">
				<EditorMenuButton
					onClick={(event) => {
						event.preventDefault()
						toggleAddingState("youtube")
						setIsOpen(false)
					}}
					isActive={activeState.isAddingYoutube}
					className="w-full"
				>
					<div className="flex w-full items-center gap-2">
						<YoutubeIcon className="h-4 w-4" />
						<div className="truncate text-xs text-primary">Youtube video</div>
					</div>
				</EditorMenuButton>
			</Tooltip>
		</DropdownMenu>
	)
}
